import { debounce, isMatch } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import isPast from 'date-fns/is_past';
import { breakpointCodes } from 'rp-mq-breakpoints';
import { bindUGCCatalogue, getUGCCatalogueItemSafe } from 'rp-bloodstock-search';
import DataTable from '../GridDataTable';
import RelativesRow from '../../components/DataTable/RelativesRow';
import MultiLineHeader from '../../components/DataTable/MultiLineHeader';
import LotCell from '../../components/DataTable/LotCell';
import DamDamSireCell from '../../components/DataTable/DamDamSireCell';
import ExpandCell from '../../components/DataTable/ExpandCell';
import ResponsiveCell from '../../components/DataTable/ResponsiveCell';
import ResponsiveHeader from '../../components/DataTable/ResponsiveHeader';
import TableBody from '../../components/DataTable/ExpandableRowsBody';
import CatalogueTableHorseCell from './CatalogueTableHorseCell';
import { expandRow } from '../../actions/expandableRow';
import * as catalogueActions from '../../actions/catalogue';
import {
    catalogueStateKey,
    cataloguePagerLength,
    isCatalogueUpcoming,
    isRefinementUsed,
    getCatalogueDataset,
} from '../../helpers/catalogueUtils';

import { updatePagerAndLotsFound } from '../../helpers/dispatchProps';
import { queryUrl } from '../../helpers/urlBuilder';

const ATTR_DATA_TITLE = 'data-title';

const ERROR_MESSAGE_DEFAULT = 'No results found';
const ERROR_MESSAGE_REFINE = `No lots found matching your search.
Use the reset button to clear your search and start again.`;

const { EXTRA_NARROW, NARROW, MEDIUM, WIDE } = breakpointCodes;
const isShortlistAvailable = (shortlist, saleEndDate) => shortlist || !isPast(saleEndDate);
const isNoteAvailable = (note, saleEndDate) => note || !isPast(saleEndDate);

function shouldUpdateDebounced(oldRefinement, newRefinement) {
    const DEBOUNCED_FIELDS = [
        'minPrice',
        'maxPrice'
    ];
    return DEBOUNCED_FIELDS.some(field => oldRefinement[field] !== newRefinement[field]);
}

const upcoming = isCatalogueUpcoming();
const {
    venueid,
    salesdate,
    salesenddate,
    lot,
    page
} = getCatalogueDataset();
const url = `/bloodstock/sales/catalogues/${venueid}/${salesdate}/data.json`;

export class CatalogueTable extends PureComponent {
    static propTypes = {
        data: PropTypes.object,
        expandedRow: PropTypes.any,
    };
    static colSpanGetter = (breakpoint) => {
        switch (breakpoint) {
            case EXTRA_NARROW:
                return 4;
            case NARROW:
                return 5;
            case MEDIUM:
            case WIDE:
                return 9;
            default:
                return upcoming ? 8 : 10;
        }
    };
    componentWillReceiveProps(nextProps) {
        const oldRefinement = this.props.refinement;
        const newRefinement = nextProps.refinement;

        if (oldRefinement !== newRefinement) {
            if (shouldUpdateDebounced(oldRefinement, newRefinement)) {
                this.updateCatalogueTableDebounced();
            } else {
                this.props.updateCatalogueTable();
            }
        }
    }

    getConfig() {
        const spacerColumn = {
            columnName: '_spacer',
            displayName: '',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'expand'
            },
            customCell: ExpandCell
        };
        const lotNoColumn = {
            columnName: 'lot_no',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'lot-no'
            },
            customHeader: {
                component: ResponsiveHeader,
                options: {
                    displayName: [
                        {
                            className: '',
                            displayName: 'Lot '
                        },
                        {
                            className: 'hidden-lg-down',
                            displayName: 'No'
                        }
                    ]
                }
            },
            customCell: {
                component: LotCell,
                options: {
                    letter: 'lot_letter'
                }
            }
        };
        const horseAndSireColumn = {
            columnName: 'horse_style_name',
            customHeader: {
                component: MultiLineHeader,
                options: {
                    displayName: upcoming
                        ? ['Horse name', 'Sire']
                        : [
                            {
                                text: 'Horse name'
                            },
                            {
                                text: 'Sire',
                                className: 'hidden-sm-down'
                            }
                        ]
                }
            },
            customCell: {
                component: props => {
                    /* eslint-disable react/prop-types */
                    const { lot_no, lot_letter, sale_date } = props.rowData;
                    /* eslint-enable react/prop-types */
                    const { shortlist, note } = getUGCCatalogueItemSafe(
                        this.props.ugc,
                        Number(venueid),
                        sale_date,
                        lot_no,
                        lot_letter
                    );
                    return (
                        <CatalogueTableHorseCell
                            shortlist={shortlist}
                            note={note}
                            {...props}
                        />
                    );
                },
                options: upcoming
                    ? {
                        horseUid: 'horse_uid',
                        horseName: 'horse_style_name',
                        sireUid: 'sire_uid',
                        sireName: 'sire_style_name',
                        entered: 'entered',
                        entryDetails: 'entry_details',
                        courseId: 'course_uid',
                        courseName: 'course_name',
                        raceDate: 'race_date',
                        raceId: 'race_uid'
                    }
                    : {
                        horseUid: 'horse_uid',
                        horseName: 'horse_style_name',
                        sireUid: 'sire_uid',
                        sireName: 'sire_style_name',
                        entered: 'entered',
                        entryDetails: 'entry_details',
                        breakClassName: 'hidden-sm-down',
                        sireClassName: 'hidden-sm-down',
                        courseId: 'course_uid',
                        courseName: 'course_name',
                        raceDate: 'race_date',
                        raceId: 'race_uid'
                    }
            },
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'horse-name'
            }
        };
        const horseColorColumn = {
            columnName: 'horse_first_colour_code',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'colour'
            },
            displayName: upcoming
                ? undefined
                : 'Col',
            customHeader: upcoming
                ? {
                    component: ResponsiveHeader,
                    options: {
                        displayName: [
                            {
                                className: 'hidden-lg-down',
                                displayName: 'Colour'
                            },
                            {
                                className: 'hidden-sm-down hidden-xl-up',
                                displayName: 'Col'
                            }
                        ]
                    }
                }
                : undefined
        };
        const horseSexColumn = {
            columnName: 'horse_sex',
            displayName: 'Sex',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'sex'
            }
        };
        const horseAgeColumn = {
            columnName: 'horse_age',
            displayName: 'Age',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'age'
            }
        };
        const damAndDamSireColumn = {
            columnName: 'dam_style_name',
            customHeader: {
                component: MultiLineHeader,
                options: {
                    displayName: ['Dam', 'Dam\'s sire']
                }
            },
            customCell: {
                component: DamDamSireCell,
                options: {
                    damUid: 'dam_uid',
                    damName: 'dam_style_name',
                    damSireUid: 'sire_of_dam_uid',
                    damSireName: 'sire_of_dam_style_name'
                }
            },
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'dam-sire'
            }
        };
        const vendorColumn = {
            columnName: 'seller_name',
            displayName: 'Vendor',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'seller-name'
            }
        };
        const priceColumn = {
            columnName: 'price',
            displayName: 'Price',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'price'
            },
            customCell: {
                component: ResponsiveCell,
                options: {
                    columns: [
                        {
                            name: 'price_styled_long'
                        }
                    ]
                }
            }
        };
        const buyerColumn = {
            columnName: 'buyer_detail',
            displayName: 'Buyer',
            headerDataAttrs: {
                [ATTR_DATA_TITLE]: 'buyer-detail'
            }
        };
        return {
            url,
            primaryColumn: 'lot_no',
            tableClass: 'table__SalesCatalogueTable',
            activeLot: lot,
            initialPage: page,
            columns: upcoming
                ? [
                    spacerColumn,
                    lotNoColumn,
                    horseAndSireColumn,
                    horseColorColumn,
                    horseSexColumn,
                    horseAgeColumn,
                    damAndDamSireColumn,
                    vendorColumn
                ]
                : [
                    spacerColumn,
                    lotNoColumn,
                    horseAndSireColumn,
                    horseColorColumn,
                    horseSexColumn,
                    horseAgeColumn,
                    damAndDamSireColumn,
                    vendorColumn,
                    priceColumn,
                    buyerColumn
                ]
        };
    }
    expansionComponent = props => (
        <RelativesRow
            colSpan={CatalogueTable.colSpanGetter(this.props.breakpointCode)}
            venueId={Number(venueid)}
            saleDate={salesdate}
            saleEndDate={salesenddate}
            damDateOfBirth={this.props.damDateOfBirth}
            className="table__SalesCatalogueRelatives"
            showShortlist={isShortlistAvailable}
            showNote={isNoteAvailable}
            {...props}
        />
    );
    getTableProps() {
        return {
            bodyComponent: TableBody,
            bodyProps: {
                expansionComponent: this.expansionComponent,
                expandedRow: this.props.expandedRow,
                onRowExpand: this.props.onRowExpand,
                scrollToRow: true,
                primaryColumn: 'lot_no',
                identity: ({ lot_no, lot_letter }) => ({
                    lot_no,
                    lot_letter
                })
            }
        };
    }
    updateCatalogueTableDebounced = debounce(() => {
        this.props.updateCatalogueTable();
    }, 500);
    render() {
        return (
            <DataTable
                config={this.getConfig()}
                stateKey={catalogueStateKey}
                errorMessage={
                    isRefinementUsed(this.props.refinement)
                        ? ERROR_MESSAGE_REFINE
                        : ERROR_MESSAGE_DEFAULT
                }
                colSpanGetter={CatalogueTable.colSpanGetter}
                onAfterUpdate={this.props.onAfterUpdate}
                tableProps={this.getTableProps()}
            />
        );
    }
}

function mapStateToProps(state) {
    let damDateOfBirth;
    const {
        refinement,
        data,
        dataTable: {
            rows
        },
        expandableRow: {
            expandedRow
        }
    } = state[catalogueStateKey];
    const breakpointCode = state.breakpoints.current.code;
    const selectedRow = rows.find(row => isMatch(row, expandedRow[0]));

    if (selectedRow) {
        damDateOfBirth = selectedRow.dam_date_of_birth;
    }

    return {
        breakpointCode,
        refinement,
        expandedRow,
        data,
        damDateOfBirth,
    };
}

const mapDispatchToProps = {
    onAfterUpdate: updatePagerAndLotsFound(catalogueStateKey, cataloguePagerLength),
    onRowExpand({ lot_no, lot_letter }) {
        if (window.history) {
            window.history.replaceState(
                {},
                window.title,
                queryUrl(window.location.href, { lot: lot_no, letter: lot_letter })
            );
        }
        return expandRow(catalogueStateKey, { lot_no, lot_letter });
    },
    ...catalogueActions
};

export default connect(mapStateToProps, mapDispatchToProps)(
    bindUGCCatalogue(CatalogueTable, catalogue => ({ ugc: catalogue }))
);



// WEBPACK FOOTER //
// ./src/js/containers/Catalogue/CatalogueTable.js